import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import { Heading, Subheading } from '../components/common/TextComponents';
import Button from '../components/common/Button';
import Navbar from '../components/layout/Navbar';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../services/firebase';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setError('');
    
    try {
      await sendPasswordResetEmail(auth, email);
      setIsSuccess(true);
      setMessage(`Password reset email sent to ${email}. Please check your inbox.`);
    } catch (err) {
      console.error('Password reset error:', err);
      
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="relative min-h-screen">
      {/* Purple gradient background with dark mode support - full height */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-50 via-pink-300 to-purple-500 dark:from-purple-900 dark:via-purple-800 dark:to-purple-900"></div>
      
      <Container className="relative">
        <Navbar
          banner={
            <a
              href="https://www.founder.university/"
              className="flex items-center gap-1 rounded-full bg-purple-950/35 px-3 py-0.5 text-sm/6 font-medium text-white hover:bg-purple-950/30 dark:bg-purple-200/20 dark:text-purple-100 dark:hover:bg-purple-200/30"
            >
              Burnt joins Founder University
            </a>
          }
        />
        
        <div className="pt-16 pb-24 sm:pt-24 sm:pb-32 md:pt-32 md:pb-48">
          <div className="mx-auto max-w-lg">
            {/* Header Section */}
            <div className="text-center mb-12">
              <Subheading className="text-gray-950/75 dark:text-gray-100/75">Forgot your password?</Subheading>
              <Heading as="h1" className="mt-2 text-gray-950 dark:text-gray-100">
                Reset your password
              </Heading>
              <p className="mt-4 text-lg/7 text-gray-950/75 dark:text-gray-100/75">
                Enter your email and we'll send you instructions to reset your password
              </p>
            </div>
            
            {/* Main Form Card */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-white/20 dark:bg-gray-900/95 dark:border-gray-700/30">
              {isSuccess ? (
                /* Success State */
                <div className="text-center">
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center dark:bg-green-900/30">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 dark:text-gray-100">Check your email</h3>
                  <p className="text-gray-600 mb-8 dark:text-gray-300">{message}</p>
                  <div className="space-y-4">
                    <Button 
                      onClick={() => {
                        setEmail('');
                        setIsSuccess(false);
                        setMessage('');
                      }}
                      variant="secondary"
                      className="w-full py-3 font-semibold"
                    >
                      Send another email
                    </Button>
                    <Link to="/auth" className="block">
                      <Button variant="outline" className="w-full py-3 font-medium">
                        Back to login
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                /* Form State */
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                      Email address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:border-purple-400"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm dark:bg-red-900/30 dark:border-red-800/50 dark:text-red-300">
                      {error}
                    </div>
                  )}
                  
                  <Button
                    type="submit"
                    className="w-full py-3 font-semibold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      'Send reset instructions'
                    )}
                  </Button>
                </form>
              )}
            </div>
            
            {/* Back to login section */}
            {!isSuccess && (
              <div className="mt-8 text-center">
                <p className="text-gray-900 font-medium dark:text-gray-100">
                  Remember your password?{' '}
                  <Link to="/auth" className="text-purple-700 hover:text-purple-600 font-semibold dark:text-purple-300 dark:hover:text-purple-200">
                    Sign in
                  </Link>
                </p>
              </div>
            )}
            
            {/* Terms and Privacy Links */}
            <p className="mt-6 text-center text-sm text-gray-800 dark:text-gray-200">
              By using our service, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:text-purple-600 font-medium dark:text-purple-300 dark:hover:text-purple-200">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:text-purple-600 font-medium dark:text-purple-300 dark:hover:text-purple-200">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ForgotPasswordPage;